import React, { useState, useEffect } from "react";
import { useUser } from "../../hooks";
import moment from "moment";
import { motion, AnimatePresence } from "framer-motion";
import styled, { keyframes } from "styled-components";
import { FiAward, FiGift, FiHeart, FiSmile, FiStar, FiSun, FiX } from "react-icons/fi";

import { useGetCurrentMemberProfileQuery, useGetCurrentUserProfileQuery } from "../../store/auth/authApi";
import { useSelector } from "react-redux";
import { selectUserType } from "../../store/auth";

const CONFETTI_ICONS = [FiGift, FiStar, FiHeart, FiSun, FiAward, FiSmile];

const float = keyframes`
  0% { transform: translateY(110vh) translateX(0) rotate(0deg); opacity: 0; }
  10% { opacity: 1; }
  90% { opacity: 1; }
  100% { transform: translateY(-20vh) translateX(40px) rotate(360deg); opacity: 0; }
`;

const BubbleContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 10001;
  overflow: hidden;
  background: rgba(0,0,0,0.02);
`;

const Bubble = styled.div`
  position: absolute;
  bottom: -100px;
  background: ${(props) => props.color || "rgba(139, 49, 58, 0.6)"};
  border-radius: 50%;
  animation: ${float} ${(props) => props.duration || "5s"} ease-in-out infinite;
  left: ${(props) => props.left || "50%"};
  width: ${(props) => props.size || "30px"};
  height: ${(props) => props.size || "30px"};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${(props) => parseInt(props.size) * 0.7 + "px"};
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  backdrop-filter: blur(2px);
  border: 1px solid rgba(255,255,255,0.3);
`;

const CelebrationText = styled(motion.div)`
  position: fixed;
  top: 40%;
  left: 0;
  width: 100vw;
  z-index: 10002;
  text-align: center;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 20px;
`;

const BirthdayCelebration = () => {
  const user = useUser();
  const userType = useSelector(selectUserType);
  const isMember = userType?.toLowerCase().includes("member");
  
  const { data: memberProfile } = useGetCurrentMemberProfileQuery(undefined, { skip: !isMember });
  const { data: staffProfile } = useGetCurrentUserProfileQuery(undefined, { skip: isMember });

  const [isBirthday, setIsBirthday] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [showMainTitle, setShowMainTitle] = useState(true);

  useEffect(() => {
    const profile = isMember ? memberProfile : staffProfile?.data;
    const dob = profile?.dob || profile?.date_of_birth || user?.dob || user?.date_of_birth;
    
    if (dob) {
      const today = moment().format("MM-DD");
      const userBirthday = moment(dob).format("MM-DD");
      
      if (today === userBirthday) {
        setIsBirthday(true);
        const timer = setTimeout(() => setShowPopup(true), 1500);
        const titleTimer = setTimeout(() => setShowMainTitle(false), 8000);
        return () => {
          clearTimeout(timer);
          clearTimeout(titleTimer);
        };
      }
    }
  }, [user, memberProfile, staffProfile, isMember]);

  if (!isBirthday) return null;

  const currentProfile = isMember ? memberProfile : staffProfile?.data;
  const firstName = currentProfile?.first_name || user?.first_name || user?.name?.split(' ')[0] || "Member";
  const lastName = currentProfile?.last_name || user?.last_name || user?.name?.split(' ').slice(1).join(' ') || "";

  const bubbles = Array.from({ length: 45 }).map((_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    size: `${Math.random() * 80 + 35}px`,
    duration: `${Math.random() * 6 + 5}s`,
    delay: `${Math.random() * 8}s`,
    color: [
      "rgba(139, 49, 58, 0.8)", // Primary
      "rgba(49, 119, 140, 0.8)", // Secondary2
      "rgba(25, 188, 49, 0.8)",  // Success
      "rgba(255, 165, 0, 0.8)",  // Orange
      "rgba(133, 67, 241, 0.8)", // Purple
      "rgba(237, 73, 224, 0.8)"  // Pink
    ][Math.floor(Math.random() * 6)],
    Icon: CONFETTI_ICONS[Math.floor(Math.random() * CONFETTI_ICONS.length)]
  }));

  return (
    <>
      <BubbleContainer>
        {bubbles.map((b) => (
          <Bubble
            key={b.id}
            left={b.left}
            size={b.size}
            duration={b.duration}
            color={b.color}
            style={{ animationDelay: b.delay }}
          >
            <b.Icon aria-hidden="true" color="#fff" size={Math.round(parseInt(b.size) * 0.55)} />
          </Bubble>
        ))}
      </BubbleContainer>

      <AnimatePresence>
        {showMainTitle && (
          <CelebrationText
            initial={{ scale: 0.5, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 1.5, opacity: 0, transition: { duration: 1.5 } }}
          >
            <h1 
              style={{ 
                fontSize: "clamp(2.5rem, 8vw, 5rem)", 
                fontWeight: "900", 
                color: "var(--color-primary)",
                textShadow: "0 10px 40px rgba(0,0,0,0.3), 0 0 60px rgba(139, 49, 58, 0.4)",
                margin: 0,
                fontFamily: "DM Sans",
                letterSpacing: "4px",
                lineHeight: "1.1"
              }}
            >
              HAPPY BIRTHDAY!
            </h1>
            <h2 
              style={{ 
                fontSize: "clamp(1.5rem, 4vw, 2.5rem)", 
                fontWeight: "600", 
                color: "var(--color-secondary)",
                textShadow: "0 5px 15px rgba(0,0,0,0.2)",
                marginTop: "10px",
                lineHeight: "1.2"
              }}
            >
              {firstName} {lastName}
            </h2>
          </CelebrationText>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showPopup && (
          <motion.div
            initial={{ scale: 0, opacity: 0, x: 100 }}
            animate={{ scale: 1, opacity: 1, x: 0 }}
            exit={{ scale: 0, opacity: 0 }}
            style={{
              position: "fixed",
              bottom: "40px",
              right: "40px",
              zIndex: 10005,
              pointerEvents: "auto"
            }}
          >
            <div 
              className="bg-white p-4 rounded-4 shadow3 text-center position-relative border-0" 
              style={{ 
                maxWidth: "380px", 
                borderTop: "8px solid var(--color-primary)",
                boxShadow: "0 25px 50px rgba(0,0,0,0.2)" 
              }}
            >
              <button 
                onClick={() => setShowPopup(false)}
                className="position-absolute border-0 bg-transparent text-blacklight p-2"
                style={{ top: "10px", right: "10px" }}
                aria-label="Close"
              >
                <FiX size={22} aria-hidden="true" />
              </button>
              <div 
                className="mx-auto mb-3 d-flex align-items-center justify-content-center rounded-circle"
                style={{ 
                  width: "85px", 
                  height: "85px", 
                  background: "linear-gradient(135deg, rgba(139, 49, 58, 0.15) 0%, rgba(49, 119, 140, 0.15) 100%)", 
                  color: "var(--color-primary)",
                  boxShadow: "inset 0 4px 10px rgba(0,0,0,0.05)"
                }}
              >
                <FiGift size={40} aria-hidden="true" />
              </div>
              <h4 className="fw-bold text-primary1 mb-1">Happy Birthday!</h4>
              <p className="fw-bold text-black1 mb-2" style={{ fontSize: "1.2rem" }}>{firstName} {lastName}</p>
              <p className="text-blacklight mb-4" style={{ lineHeight: "1.6" }}>
                The entire community wishes you a spectacular day! May your year be filled with abundance, joy, and all the success you deserve.
              </p>
              <div className="d-flex justify-content-center gap-2">
                <button 
                  className="btn text-white rounded-pill px-5 py-2 fw-bold"
                  style={{ 
                    backgroundColor: "var(--color-primary)",
                    boxShadow: "0 10px 20px rgba(139, 49, 58, 0.3)",
                    transition: "all 0.3s ease"
                  }}
                  onClick={() => setShowPopup(false)}
                  onMouseOver={(e) => e.target.style.transform = "scale(1.05)"}
                  onMouseOut={(e) => e.target.style.transform = "scale(1)"}
                >
                  Thank you!
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default BirthdayCelebration;
